import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search } from 'carbon-components-react';
import ResultsBox from './resultsBox';
import { OptionItem } from 'types';
import Util from 'utilities/helper.util';
import { useEventListener } from 'utilities/hooks.util';

const SearchAutoSuggestPanel: React.FC<Props> = ({
  id,
  size,
  maxItem,
  items = [],
  labelText = '',
  placeHolderText,
  defaultInputValue = '',
  onSelectItem,
  onChangeInput,
  onBlurInput,
  remoteFilter
}) => {
  const [searchInputValue, setSearchInputValue] = useState<string>(
    defaultInputValue
  );
  const [suggestion, setSuggestion] = useState<any[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isShowResult, setIsShowResult] = useState<Boolean>(false);
  const debouncedFunction = useCallback(
    Util.debounce(async (args: string[]) => {
      const value = args[0];
      if (typeof remoteFilter === 'function') {
        remoteFilter(value);
      } else {
        const result = items.filter(
          (item) =>
            item.label.toLocaleLowerCase().search(value.toLocaleLowerCase()) !==
            -1
        );
        const final =
          typeof maxItem === 'number' ? result.slice(0, maxItem) : result;
        setSuggestion(final);
      }
    }, 500),
    [items, remoteFilter, maxItem]
  );

  useEffect(() => {
    setIsShowResult(Array.isArray(suggestion) && suggestion.length > 0);
  }, [suggestion]);

  const onClickResult = (item: OptionItem) => {
    if (item.value) {
      setSearchInputValue(item.label);
      typeof onChangeInput === 'function' && onChangeInput(item.label);
      typeof onSelectItem === 'function' && onSelectItem(item);
    }
    setIsShowResult(false);
  };

  const handleInputChange = (value: string) => {
    if (value) {
      debouncedFunction(value);
    } else {
      setSuggestion([]);
      typeof onChangeInput === 'function' && onChangeInput('');
    }
    setSearchInputValue(value);
  };

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (containerRef.current && evt.target) {
        if (!containerRef.current.contains(evt.target)) {
          setIsShowResult(false);
        }
      }
    },
    [containerRef]
  );

  useEffect(() => {
    defaultInputValue && setSearchInputValue(defaultInputValue);
  }, [defaultInputValue]);

  useEffect(() => {
    if (typeof remoteFilter === 'function' && Array.isArray(items)) {
      setSuggestion(items);
    }
  }, [items, remoteFilter]);

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  return (
    <div ref={containerRef} className={'search-auto-suggest--wrapper'}>
      {labelText && <label className="bx--label">{labelText}</label>}
      <Search
        id={`${id}-input`}
        size={size}
        labelText={labelText}
        placeHolderText={placeHolderText}
        value={searchInputValue}
        onFocus={(evt) => handleInputChange(evt.target.value)}
        onChange={(evt) => handleInputChange(evt.target.value)}
        onBlur={(evt) =>
          typeof onBlurInput === 'function' && onBlurInput(evt.target.value)
        }
      />
      <div className="search-auto-suggest--searchItems">
        {isShowResult && (
          <ResultsBox id={id} items={suggestion} onItemClick={onClickResult} />
        )}
      </div>
    </div>
  );
};

type Props = {
  id: string;
  size?: 'lg' | 'sm' | 'xl';
  maxItem?: number;
  labelText?: string;
  items?: OptionItem[];
  placeHolderText?: string;
  defaultInputValue?: string;
  onChangeInput?: Function;
  onSelectItem?: Function;
  onBlurInput?: Function;
  remoteFilter?: Function;
};

export default React.memo(SearchAutoSuggestPanel);
